import { sessionSchema, type Session } from '../schemas/session.schema';

const storageKey = 'els.auth.session';

const decodeBase64Url = (value: string) => {
  const base64 = value.replaceAll('-', '+').replaceAll('_', '/');
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  return atob(padded);
};

const getJwtExp = (token: string): number | null => {
  const [, payload] = token.split('.');
  if (!payload) {
    return null;
  }

  try {
    const decoded: unknown = JSON.parse(decodeBase64Url(payload));
    if (
      typeof decoded === 'object' &&
      decoded !== null &&
      'exp' in decoded &&
      typeof decoded.exp === 'number'
    ) {
      return decoded.exp;
    }
    return null;
  } catch {
    return null;
  }
};

export const isJwtCurrent = (token: string, now = Date.now()) => {
  const exp = getJwtExp(token);
  return exp !== null && exp * 1000 > now;
};

export const isJwtExpiringSoon = (token: string, thresholdMs: number, now = Date.now()) => {
  const exp = getJwtExp(token);
  if (exp === null) {
    return true;
  }
  return exp * 1000 - now < thresholdMs;
};

export const isSessionCurrent = (session: Session | null) =>
  session ? isJwtCurrent(session.access_token) : false;

export const getJwtSub = (token: string): string | null => {
  const [, payload] = token.split('.');

  if (!payload) {
    return null;
  }

  try {
    const decoded: unknown = JSON.parse(decodeBase64Url(payload));
    if (
      typeof decoded === 'object' &&
      decoded !== null &&
      'sub' in decoded &&
      typeof decoded.sub === 'string'
    ) {
      return decoded.sub;
    }
    return null;
  } catch {
    return null;
  }
};

export const getJwtRole = (token: string): string | null => {
  const [, payload] = token.split('.');

  if (!payload) {
    return null;
  }

  try {
    const decoded: unknown = JSON.parse(decodeBase64Url(payload));
    if (
      typeof decoded === 'object' &&
      decoded !== null &&
      'role' in decoded &&
      typeof decoded.role === 'string'
    ) {
      return decoded.role;
    }
    return null;
  } catch {
    return null;
  }
};

export const getJwtOrganizations = (token: string): string[] => {
  const [, payload] = token.split('.');

  if (!payload) {
    return [];
  }

  try {
    const decoded: unknown = JSON.parse(decodeBase64Url(payload));
    if (
      typeof decoded === 'object' &&
      decoded !== null &&
      'organizations' in decoded &&
      Array.isArray(decoded.organizations)
    ) {
      return decoded.organizations.filter((org): org is string => typeof org === 'string');
    }
    return [];
  } catch {
    return [];
  }
};

export const readStoredSession = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const rawSession = window.sessionStorage.getItem(storageKey);

  if (!rawSession) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(rawSession);
    const session = sessionSchema.parse(parsed);

    if (isSessionCurrent(session)) {
      return session;
    }
  } catch {
    window.sessionStorage.removeItem(storageKey);
    return null;
  }

  window.sessionStorage.removeItem(storageKey);
  return null;
};

export const writeStoredSession = (session: Session) => {
  window.sessionStorage.setItem(storageKey, JSON.stringify(session));
};

export const clearStoredSession = () => {
  window.sessionStorage.removeItem(storageKey);
};
